import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { StoreContext } from '../../../components/Store';
import { dateFormatChange } from '../../../utils/date';

import { Stack, Tabs, Tab, Box, Container, Paper, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { getAccountDetails } from '../../../api/settings';
import { getConsigmentDetails, getShipmentStatusForShipmentView, getErrorsDetails } from '../../../api/dashboard';

import ShipmentErrorBox from '../../../components/ShipmentErrorBox';
import ShipmentErrorModal from '../../../components/ShipmentErrorModal';
import { DuplicateModal, CancelShipmentModal, AmendShipmentModal } from '../../Decs/Modals/MenuBarModals';
import RefreshStatusModal from '../../Decs/Modals/RefreshStatusModal';

import { statuses, READ_ONLY_LABEL } from '../../../utils/constants';
import { getAmendOrNot, getShipmentDeleteOrCancel } from '../../../api/util';
import { isEORIUser, is360PayStaff } from '../../../utils/roles';

const paginationSettings = { page: 0, pageSize: 50 };

export default function ShipmentView() {
  const { state } = useLocation();
  const store = useContext(StoreContext);
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState({});
  const [logoImage, setLogoImage] = useState('');
  const [cancelOrDelete, setCancelOrDelete] = useState('');
  const [isAmendable, setIsAmendable] = useState(false);

  const [shipmentData, setShipmentData] = useState([]);
  const [consignmentData, setConsignmentData] = useState([]);
  const [errorData, setErrorData] = useState([]);

  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [cancelShipmentModalOpen, setCancelShipmentModalOpen] = useState(false);
  const [amendShipmentModalOpen, setAmendShipmentModalOpen] = useState(false);
  const [refreshStatusModalOpen, setRefreshStatusModalOpen] = useState(false);

  const [isReadOnly, setIsReadOnly] = useState(false);

  const [shouldFetchData, setShouldFetchData] = useState(true);

  const apiCallForShipmentData = async (headerId, companyId) => {
    await getShipmentStatusForShipmentView(headerId, companyId).then((data) => {
      setShipmentData(data.data);
    });
  };
  const getConsignmentDetailsData = async (headerId, companyId) => {
    await getConsigmentDetails(headerId, companyId).then((data) => {
      setConsignmentData(data.data);
    });
  };
  const apiCallForAccountDetails = async () => {
    await getAccountDetails(state.companyId).then((data) => {
      if (state.companyId == data.companyId) {
        if (isEORIUser()) {
          let binary = '';
          if (data.logo) {
            let bytes = new Uint8Array(data.logo?.data);
            let len = bytes.byteLength;
            for (let j = 0; j < len; j++) {
              binary += String.fromCharCode(bytes[j]);
            }
            setLogoImage(btoa(binary));
          }
        }

        setCompanyName({
          companyName: data.company,
          companyEori: data.eori,
        });
      }

      if (data.customerTypeId === 40 || is360PayStaff()) {
        setIsReadOnly(true);
      }
    });
  };

  const apiCallCancelOrDelete = async (headerId) => {
    const response = await getShipmentDeleteOrCancel(headerId);
    const { cancelShipment, deleteShipment } = response[0];
    let action = 'none';
    if (cancelShipment) {
      action = 'cancel';
    } else if (deleteShipment) {
      action = 'delete';
    }
    setCancelOrDelete(action);
  };

  const apiCallForAmendOrNot = async (headerId) => {
    const response = await getAmendOrNot(headerId);
    setIsAmendable(!!response[0]['ammendAllowed']);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        await apiCallForShipmentData(state.res, state.companyId);
        await getConsignmentDetailsData(state.res, state.companyId);
        await apiCallForAccountDetails();
        await apiCallCancelOrDelete(state.res);
        await apiCallForAmendOrNot(state.res);
      } catch (err) {
        console.log(err);
      }
    }

    if (shouldFetchData) {
      fetchData();
      setShouldFetchData(false);
    }
  }, [shouldFetchData]);

  const modeOfTransport = shipmentData.length ? shipmentData[0]?.transportTypeDescription : '';
  const modeOfTransportType = modeOfTransport?.includes('UNACCOMPANIED') ? 'unaccompanied' : 'accompanied';

  const { isAccepted, isError, isRejected } = useMemo(() => {
    let acc = false;
    let err = false;
    let rej = false;
    const updateStatuses = (i) => {
      if (i.status.toUpperCase() === statuses.ACCEPTED) acc = true;
      if (i.status.toUpperCase() === statuses.ERRORS) err = true;
      if (i.status.toUpperCase() === statuses.REJECTED) rej = true;
    };
    shipmentData.forEach((i) => updateStatuses(i));
    consignmentData.forEach((i) => updateStatuses(i));
    return { isAccepted: acc, isError: err, isRejected: rej };
  }, [shipmentData, consignmentData]);

  const showErrorModal = () => {
    try {
      setErrorModalOpen(true);
      getErrorsDetails(state.res).then((data) => setErrorData(data));
    } catch (e) {
      console.error(e);
    }
  };

  const shipmentColumns = [
    {
      field: 'clientJobNumber',
      headerName: 'Your Reference',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
    },
    {
      field: 'vehicleIdentity',
      headerName: 'Vehicle Identity',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
    },
    {
      field: 'departureDate',
      headerName: 'Departure Date',
      type: 'number',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <div>{params.row.departureDate ? dateFormatChange(params.row.departureDate) : ''}</div>,
    },
    {
      field: 'arrivalDateTime',
      headerName: 'Arrival Date',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <div>{params.row.arrivalDateTime ? dateFormatChange(params.row.arrivalDateTime) : ''}</div>
      ),
    },
    {
      field: 'totalConsignments',
      headerName: 'Number of Consignments',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      align: 'center',
      headerAlign: 'center',
    },
    ...(modeOfTransportType === 'unaccompanied'
      ? [{ field: 'mrn', headerName: 'MRN', headerClassName: 'table-header', flex: 1 }]
      : []),
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box
            sx={{
              backgroundColor: {
                [statuses.ACCEPTED]: '#AEDD94',
                [statuses.ERRORS]: '#FFB4B0',
                [statuses.REJECTED]: '#fff',
                [null]: '#fff',
              }[params.value.toUpperCase()],
              width: '100%',
              height: '100%',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'click',
      headerName: '',
      width: 50,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (
        <ExitToAppIcon
          sx={{ cursor: 'pointer', color: '#1464F6' }}
          onClick={() =>
            navigate('/EnsDeclaration', {
              state: {
                headerId: state.res,
                companyId: state.companyId,
                error: params.row.status.toUpperCase() === statuses.ERRORS,
                image: logoImage,
                currentCompany: companyName,
                isReadOnly,
              },
            })
          }
          className="svg-icons"
        />
      ),
    },
  ];
  const consignmentColumns = [
    {
      field: 'consignmentNumber',
      headerName: '#',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      editable: false,
      headerClassName: 'table-header',
      width: 75,
      minWidth: 75,
      maxWidth: 75,
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'lrn',
      headerName: 'Reference',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'consignor',
      headerName: 'Consignor',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'consignee',
      headerName: 'Consignee',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'consignmentReference',
      headerName: 'Consignment Reference',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'totalItemsCounts',
      headerName: 'Number of Lines',
      type: 'number',
      flex: 1,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'table-header',
      renderCell: (params) => {
        return (
          <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
            {params.value}
          </Box>
        );
      },
    },
    ...(modeOfTransportType === 'accompanied'
      ? [
          {
            field: 'mrn',
            headerName: 'MRN',
            flex: 1,
            editable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'table-header',
            renderCell: (params) => {
              return (
                <Box sx={{ ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
                  {params.value}
                </Box>
              );
            },
          },
        ]
      : []),

    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box
            sx={{
              fontWeight: 'bold',
              ...(params.row.status.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}),
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: '23',
      headerName: '',
      width: 50,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (
        <div>
          <ExitToAppIcon
            onClick={() =>
              navigate('/EnsDeclaration', {
                state: {
                  consignmentId: params.row.id,
                  companyId: state.companyId,
                  headerId: state.res,
                  error: params.row.status.toUpperCase() == statuses.ERRORS,
                  image: logoImage,
                  currentCompany: companyName,
                  isReadOnly,
                },
              })
            }
            style={{ color: '#1464F6', cursor: 'pointer' }}
          />
        </div>
      ),
    },
  ];

  const renderCancelOrDelete = () => {
    if (cancelOrDelete === 'cancel') {
      return <Tab label="Cancel Shipment" onClick={() => setCancelShipmentModalOpen(true)} disabled={isReadOnly} />;
    }
    if (cancelOrDelete === 'delete') {
      return <Tab label="Delete Shipment" onClick={() => setCancelShipmentModalOpen(true)} disabled={isReadOnly} />;
    }
    return null;
  };

  const accompaniedName = shipmentData?.[0]?.transportTypeDescription === 'ROAD' ? 'a ROAD' : 'an ACCOMPANIED RORO';

  return (
    <Container
      maxWidth="xl"
      sx={{
        p: 4,
        mt: 0,
        mb: 6,
      }}
    >
      {/*-------------Modals--------------------*/}
      <ShipmentErrorModal open={errorModalOpen} setOpen={setErrorModalOpen} errorData={errorData} />
      <DuplicateModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} headerId={state.res} />
      <AmendShipmentModal
        open={amendShipmentModalOpen}
        onClose={() => setAmendShipmentModalOpen(false)}
        headerId={state.res}
      />
      <CancelShipmentModal
        open={cancelShipmentModalOpen}
        onClose={() => setCancelShipmentModalOpen(false)}
        headerId={state.res}
        reference={shipmentData[0]?.clientJobNumber}
        cancelOrDelete={cancelOrDelete}
      />
      <RefreshStatusModal
        isOpen={refreshStatusModalOpen}
        setIsOpen={setRefreshStatusModalOpen}
        headerId={state.res}
        onRefreshData={() => setShouldFetchData(true)}
      />

      <div className="shipment-view-header">
        <div className="shipment-view-header-element">EU ICS2</div>
        <div className="shipment-view-header-element">
          {companyName.companyName} {companyName.companyEori} {isReadOnly && <>{READ_ONLY_LABEL}</>}
        </div>
      </div>

      {/*------------Main Wrapper--------------*/}
      <Stack direction="row" gap={1} className="shipment-view-wrapper">
        <div className="shipment-view-panel">
          <ShipmentErrorBox isError={isError} isRejected={isRejected} showErrorModal={showErrorModal} />

          {/*--------Shipment Actions Left Menu -----------*/}
          <Tabs
            className="shipment-view-menu"
            orientation="vertical"
            TabIndicatorProps={{ style: { display: 'none' } }}
            value={0}
          >
            <div className="shipment-view-menu-header">Shipment Actions</div>
            {isAccepted && (
              <Tab
                label="Print this Shipment"
                onClick={() =>
                  navigate('/printShipment', {
                    state: {
                      shipmentDataView: shipmentData,
                      consignmentDataView: consignmentData,
                      companyName: companyName.companyName,
                    },
                  })
                }
              />
            )}
            <Tab label="Duplicate this Shipment" onClick={() => setDuplicateModalOpen(true)} disabled={isReadOnly} />
            {isAmendable && (
              <Tab
                label="Amend Shipment"
                onClick={() => {
                  setAmendShipmentModalOpen(true);
                }}
                disabled={isReadOnly}
              />
            )}
            {renderCancelOrDelete()}
            <Tab label="Refresh Status" onClick={() => setRefreshStatusModalOpen(true)} disabled={isReadOnly} />
          </Tabs>

          {/*-----------Consignment Actions Left Menu----------*/}
          <Tabs
            className="shipment-view-menu"
            orientation="vertical"
            TabIndicatorProps={{ style: { display: 'none' } }}
            value={0}
          >
            <div>
              <div className="shipment-view-menu-header">Consignment Actions</div>
            </div>
            <Tab
              label="Update a Consignment"
              onClick={() =>
                navigate('/EnsDeclaration', {
                  state: { activeStep: 'Consignments', headerId: state.res, companyId: state.companyId },
                })
              }
              disabled={isReadOnly}
            />
          </Tabs>
        </div>
        {/*------------Shipment View Table-------------*/}
        <div className="shipment-view-table-wrapper">
          <Typography id="modal-modal-title" variant="h6" component="h6" style={{ marginLeft: 0, marginBottom: 30 }}>
            {modeOfTransportType === 'accompanied'
              ? `This is ${accompaniedName} Declaration, all consignments must receive an individual MRN.`
              : 'This is an UNACCOMPANIED RORO Declaration, all consignments are accepted on one MRN.'}
          </Typography>

          <Paper sx={{ width: '100%' }}>
            <DataGridPro
              hideFooter
              rows={shipmentData}
              columns={shipmentColumns}
              getRowId={(row) => row?.headerId}
              initialState={{ pagination: { paginationModel: paginationSettings } }}
              pageSizeOptions={[20, 50, 100]}
              sx={{ border: 0 }}
              getRowClassName={(params) => {
                return params.row.status.toUpperCase() === statuses.ERRORS
                  ? 'dashboard-table-highlight'
                  : 'dashboard-table-non-highlight';
              }}
            />
          </Paper>
          <div
            style={{
              textAlign: 'left',
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '10px',
              width: '100%',
              marginTop: '20px',
            }}
          >
            Consignment Details
          </div>
          <Paper sx={{ width: '100%' }}>
            <DataGridPro
              pagination
              rows={consignmentData}
              columns={consignmentColumns}
              getRowId={(row) => row && Math.floor(Math.random() * 100000000)}
              initialState={{ pagination: { paginationModel: paginationSettings } }}
              pageSizeOptions={[20, 50, 100]}
              sx={{ border: 0 }}
            />
          </Paper>
        </div>
      </Stack>
    </Container>
  );
}
ShipmentView.propTypes = {
  countForApi: PropTypes.string,
};
